import React, { useEffect } from 'react';
import {
  ShieldCheck, Globe, Briefcase, Cpu, GraduationCap, Plane, ChevronRight,
} from 'lucide-react';
import SEO from './seo/SEO';

/**
 * GlobalEprcPage
 *
 * Dedicated brand page implementing SEO plan §4: a single page whose main
 * topic is the GlobalEPRC brand itself, so that a search for "GlobalEPRC",
 * "Global EPRC", or "Global Ethical People Resourcing Consultancy" has a
 * clear, on-topic page to rank.
 *
 * Routed at /global-eprc/ in App.js.
 */
const GlobalEprcPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    { icon: Briefcase, name: 'Manpower & Recruitment', href: '/manpower-recruitment' },
    { icon: ShieldCheck, name: 'HR Outsourcing', href: '/hr-outsourcing' },
    { icon: Cpu, name: 'IT Solutions', href: '/it-solutions' },
    { icon: GraduationCap, name: 'Skill Development & Training', href: '/skill-upgradation' },
    { icon: Plane, name: 'Travel & Tourism', href: '/travel-tourism' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 overflow-x-hidden">
      <SEO
        title="GlobalEPRC | Global Ethical People Resourcing Consultancy"
        description="GlobalEPRC, also known as Global EPRC and GEPRC, is Global Ethical People Resourcing Consultancy — providing professional manpower, recruitment, outsourcing, IT solutions, skill development, and travel & tourism services."
        path="/global-eprc/"
      />

      <section className="pt-48 pb-20 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col items-center text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-1.5 rounded-full border border-amber-100 mb-6 uppercase tracking-widest text-xs font-bold">
              The GlobalEPRC Brand
            </div>

            <h1 className="text-4xl md:text-6xl font-black text-slate-900 mb-6 leading-tight">
              GlobalEPRC
            </h1>

            <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-3xl">
              GlobalEPRC, also known as <strong>Global EPRC</strong> and <strong>GEPRC</strong>, is{' '}
              <strong>Global Ethical People Resourcing Consultancy</strong>, providing professional
              manpower, recruitment, outsourcing, IT solutions, skill development, and travel &amp;
              tourism services.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-950 text-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto text-center">
            <div>
              <p className="text-amber-400 font-bold uppercase tracking-widest text-xs mb-2">Primary Brand</p>
              <p className="text-2xl font-black">GlobalEPRC</p>
            </div>
            <div>
              <p className="text-amber-400 font-bold uppercase tracking-widest text-xs mb-2">Full Name</p>
              <p className="text-2xl font-black">Global Ethical People Resourcing Consultancy</p>
            </div>
            <div>
              <p className="text-amber-400 font-bold uppercase tracking-widest text-xs mb-2">Abbreviation</p>
              <p className="text-2xl font-black">GEPRC</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8">
          <h2 className="text-3xl md:text-4xl font-black text-center text-slate-900 mb-12">
            What <span className="text-gold-gradient">GlobalEPRC</span> Does
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 max-w-6xl mx-auto">
            {services.map(({ icon: Icon, name, href }) => (
              <a
                key={name}
                href={href}
                className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 hover:border-amber-200 hover:shadow-lg transition-all flex flex-col items-center text-center gap-4"
              >
                <div className="p-3 rounded-2xl bg-amber-50 text-amber-600">
                  <Icon className="h-6 w-6" />
                </div>
                <span className="font-semibold text-slate-800">{name}</span>
                <span className="text-amber-600 text-sm font-medium flex items-center gap-1">
                  Learn more <ChevronRight className="h-4 w-4" />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white border-t border-slate-100">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl text-center">
          <Globe className="h-10 w-10 text-amber-500 mx-auto mb-6" />
          <p className="text-slate-600 text-lg leading-relaxed">
            GlobalEPRC operates across India, Nepal, Sri Lanka, Bangladesh and Africa, connecting
            skilled professionals with organizations that need them — under one ethical,
            accountable brand.
          </p>
        </div>
      </section>
    </div>
  );
};

export default GlobalEprcPage;
